import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  NovidadeDaCarteira,
  StatusNovidadeFiltro,
  fetchNovidades,
  marcarNovidadeLida,
  marcarTodasNovidadesLidas,
} from '../../api/carteira/novidadesApi';
import { extrairMensagemErro } from '../../utils/apiError';
import './SinoNovidades.css';

/**
 * Sino de novidades na navegação global (3ª entrega da at-53, pedido do Victor de 08/09/2026).
 *
 * Antes as novidades eram uma seção no topo de `/carteira`. O Victor recusou esse desenho: a
 * carteira é sobre a carteira, e aviso é aviso — tem de estar acessível de qualquer tela, não só
 * de uma. O conteúdo é o mesmo (os textos continuam vindo PRONTOS do backend, convenção
 * at-24/at-32); o que mudou é onde ele mora.
 *
 * ## Duas decisões de comportamento
 *
 * 1. **O badge conta as NÃO LIDAS, e é global** — não acompanha paginação nem filtro. É o número
 *    que o consultor olha de relance para decidir se abre.
 * 2. **Sem badge quando não há nada**, em vez de um "0": zero em destaque é ruído permanente no
 *    cabeçalho e treina a pessoa a ignorar o sino.
 *
 * ⚠️ `fetchNovidades` **não lança** de propósito (contrato dela): falha de rede deixa o sino sem
 * badge, e não derruba a navegação nem enche a tela de toast. As ações do usuário — marcar lida —
 * essas sim avisam quando falham, porque ele clicou e precisa saber.
 */

const LIMITE = 10;

/** De quanto em quanto tempo o badge se atualiza sozinho. */
const INTERVALO_DE_ATUALIZACAO_MS = 60_000;

const IconeSino: React.FC = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
    <path
      d="M12 22a2.2 2.2 0 0 0 2.2-2.2H9.8A2.2 2.2 0 0 0 12 22Zm6.4-5.3v-5.2c0-3.2-1.7-5.8-4.6-6.5v-.7a1.8 1.8 0 1 0-3.6 0v.7C7.3 5.7 5.6 8.3 5.6 11.5v5.2L3.8 18.5v.9h16.4v-.9Z"
      fill="currentColor"
    />
  </svg>
);

function formatarQuando(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '';
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  /**
   * Altura do PendenciasBanner, medida pelo NavGlobal.
   *
   * ⚠️ Vem de fora de propósito: o sino e o hambúrguer dividem a mesma faixa do
   * topo, e duas medições independentes da mesma faixa divergiriam (o banner tem
   * altura variável e nem sempre existe).
   *
   * ⚠️ Ignorado no modo `embutido` — lá quem posiciona é o cabeçalho.
   */
  offsetTopo?: number;
  /**
   * Modo EMBUTIDO (at-56): o sino entra no **fluxo** do cabeçalho da tela, em vez
   * de flutuar no canto com `position: fixed`.
   *
   * 🔴 Existe porque o modo flutuante **colide com a ação da tela**: em `/adesoes`
   * o `PageHeader` tem o botão "Nova Adesão +" à direita, e o sino caía por cima
   * dele (visto por Victor em 09/09/2026). Não é ajuste de `right`: qualquer
   * deslocamento fixo quebraria na próxima tela com um botão de outra largura.
   *
   * 📌 É o começo da mudança que o `13-ui-kit.md` já previa — "quando a lista de
   * rotas migradas cobrir o app, o sino se muda para o shell". Nas rotas com
   * shell, quem o monta é o `ShellSaac`; o `NavGlobal` não o renderiza.
   */
  embutido?: boolean;
}

const SinoNovidades: React.FC<Props> = ({ offsetTopo = 0, embutido = false }) => {
  const [aberto, setAberto] = useState(false);
  const [itens, setItens] = useState<NovidadeDaCarteira[]>([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [status, setStatus] = useState<StatusNovidadeFiltro>('NAO_LIDAS');
  const [carregando, setCarregando] = useState(false);
  const [processando, setProcessando] = useState(false);
  const painelRef = useRef<HTMLDivElement | null>(null);

  const carregar = useCallback(async (filtro: StatusNovidadeFiltro) => {
    setCarregando(true);
    const resposta = await fetchNovidades({ limit: LIMITE, status: filtro });
    setItens(resposta.items);
    setNaoLidas(resposta.naoLidas);
    setTotalItems(resposta.totalItems);
    setCarregando(false);
  }, []);

  // Badge vivo: carrega ao montar e se atualiza sozinho. Sem isso o consultor só saberia de uma
  // novidade ao recarregar a página — e o aviso da oficina chega a qualquer momento.
  useEffect(() => {
    void carregar(status);
    const timer = setInterval(() => void carregar(status), INTERVALO_DE_ATUALIZACAO_MS);
    return () => clearInterval(timer);
  }, [carregar, status]);

  // Fecha ao clicar fora — o painel é um dropdown, não um modal: prender o clique da tela inteira
  // atrapalharia quem só quis voltar ao trabalho.
  useEffect(() => {
    if (!aberto) return undefined;

    const aoClicar = (evento: MouseEvent) => {
      if (painelRef.current && !painelRef.current.contains(evento.target as Node)) setAberto(false);
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAberto(false);
    };

    document.addEventListener('mousedown', aoClicar);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicar);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto]);

  const marcarUma = async (id: string) => {
    setProcessando(true);
    try {
      await marcarNovidadeLida(id);
      await carregar(status);
    } catch (erro) {
      toast.error(extrairMensagemErro(erro, 'Não foi possível marcar a novidade como lida.'));
    } finally {
      setProcessando(false);
    }
  };

  const marcarTodas = async () => {
    setProcessando(true);
    try {
      const marcadas = await marcarTodasNovidadesLidas();
      toast.success(
        marcadas > 0 ? `${marcadas} novidade(s) marcada(s) como lida(s).` : 'Nenhuma novidade pendente.',
      );
      await carregar(status);
    } catch (erro) {
      toast.error(extrairMensagemErro(erro, 'Não foi possível marcar as novidades como lidas.'));
    } finally {
      setProcessando(false);
    }
  };

  const bloqueado = processando || carregando;
  const mostrandoHistorico = status === 'TODAS';

  return (
    <div
      className={embutido ? 'sino-novidades sino-novidades--embutido' : 'sino-novidades'}
      ref={painelRef}
      /* No modo embutido o `top` não é definido: o container deixa de ser
         `fixed` (ver o CSS) e quem posiciona é o cabeçalho. */
      style={embutido ? undefined : { top: `calc(${offsetTopo}px + var(--nav-global-margem-topo))` }}
    >
      <button
        type="button"
        className="sino-novidades-botao"
        onClick={() => setAberto((v) => !v)}
        aria-label={naoLidas > 0 ? `Novidades (${naoLidas} não lidas)` : 'Novidades'}
        title="Novidades"
      >
        <IconeSino />
        {naoLidas > 0 && <span className="sino-novidades-badge">{naoLidas > 99 ? '99+' : naoLidas}</span>}
      </button>

      {aberto && (
        <div className="sino-novidades-painel" role="dialog" aria-label="Novidades">
          <div className="sino-novidades-cabecalho">
            <strong>Novidades</strong>
            <div className="sino-novidades-acoes">
              <button
                type="button"
                className="sino-novidades-link"
                disabled={bloqueado}
                onClick={() => setStatus(mostrandoHistorico ? 'NAO_LIDAS' : 'TODAS')}
              >
                {mostrandoHistorico ? 'Ver só as novas' : 'Ver histórico'}
              </button>
              {naoLidas > 0 && (
                <button
                  type="button"
                  className="sino-novidades-link"
                  disabled={bloqueado}
                  onClick={marcarTodas}
                >
                  Marcar todas
                </button>
              )}
            </div>
          </div>

          {itens.length === 0 && (
            <div className="sino-novidades-vazio">
              {carregando
                ? 'Carregando...'
                : mostrandoHistorico
                  ? 'Nenhuma novidade registrada.'
                  : 'Nenhuma novidade nova.'}
            </div>
          )}

          <ul className="sino-novidades-lista">
            {itens.map((novidade) => (
              <li
                key={novidade.id}
                className={
                  novidade.lidoEm ? 'sino-novidades-item sino-novidades-item--lida' : 'sino-novidades-item'
                }
              >
                <div className="sino-novidades-texto">
                  {/* Texto pronto do backend — a tela não reescreve regra. */}
                  <div className="sino-novidades-item-titulo">{novidade.titulo}</div>
                  {novidade.detalhe && <div className="sino-novidades-sub">{novidade.detalhe}</div>}
                  <div className="sino-novidades-quando">{formatarQuando(novidade.criadoEm)}</div>
                </div>

                {!novidade.lidoEm && (
                  <button
                    type="button"
                    className="sino-novidades-link"
                    disabled={bloqueado}
                    onClick={() => marcarUma(novidade.id)}
                  >
                    Lida
                  </button>
                )}
              </li>
            ))}
          </ul>

          {totalItems > itens.length && (
            <div className="sino-novidades-sub sino-novidades-rodape">
              Mostrando {itens.length} de {totalItems}.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SinoNovidades;
